/**
 * Federation — everything partner-facing: partner lifecycle and tokens, the
 * identity-provider connection toggles behind them, and the activity log.
 *
 * The tab lives in the URL so a link to the activity log lands on the activity
 * log. Partner *approval* is not here — it moved to the review queue; what
 * stays is management of partners that already exist.
 *
 * Two tabs, not three. Partners had a tab of its own because its rows carried
 * five lifecycle actions and a reveal-once token; those moved to
 * `/federation/partners/:id`, and what is left of a partner — who it is, whether
 * it works — is a row in Connections beside the platform's own IdP toggles.
 */
import { useSearchParams } from 'react-router-dom';

import { useAuth } from '../../auth/AuthContext.js';
import { useRecordPanel } from '../../components/shell/record.js';
import { Button } from '../../ui/Button.js';
import { ListShape } from '../../ui/shapes.js';
import { Tabs, type TabItem } from '../../ui/Tabs.js';
import { ActivityTab } from './ActivityTab.js';
import { ConnectionsTab } from './ConnectionsTab.js';

type FederationTab = 'connections' | 'activity';

const TABS: TabItem<FederationTab>[] = [
  { value: 'connections', label: 'Connections' },
  { value: 'activity', label: 'Activity' },
];

const VALID = new Set<string>(TABS.map((t) => t.value));

export function FederationPage() {
  const { isPlatformAdmin } = useAuth();
  const panel = useRecordPanel();
  const [params, setParams] = useSearchParams();
  const fromUrl = params.get('tab');
  const tab: FederationTab =
    fromUrl && VALID.has(fromUrl) ? (fromUrl as FederationTab) : 'connections';

  function select(next: FederationTab) {
    setParams(next === 'connections' ? {} : { tab: next }, { replace: true });
  }

  return (
    <ListShape
      title="Federation"
      subtitle="Identity connections and partner activity. A partner waiting on approval is a row on the Overview board."
      action={
        isPlatformAdmin ? (
          <Button
            variant="primary"
            icon="add"
            onClick={() => panel.openForm({ name: 'create-partner' })}
          >
            Create partner
          </Button>
        ) : undefined
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 22, paddingBottom: 64 }}>
        <Tabs items={TABS} value={tab} onChange={select} style={{ marginBottom: 16 }} />

        {tab === 'connections' && <ConnectionsTab />}
        {tab === 'activity' && <ActivityTab />}
      </div>
    </ListShape>
  );
}
